<template>
  <v-container
    fill-height
    justify-content-start
    :class="{ 'pa-7': $vuetify.breakpoint.xsOnly }"
    class="section-width pb-0"
  >
    <v-layout column>

      <div
        class="reservation-tabs-margin">
        <v-btn-toggle
          class="reservation-list"
          v-model="selectedType"
          borderless
          mandatory
        >
          <v-btn :value="activityReservationTypes[0]">
            <span>{{ $t("pages.reservation_list_types." + activityReservationTypes[0].toLowerCase()) }}</span><div v-if="!loadingReservations && totalReservations !== null" class="attendees_badge badge__big">{{ totalReservations }}</div>
          </v-btn>
          <v-btn :value="activityReservationTypes[1]">
            <span>{{ $t("pages.reservation_list_types." + activityReservationTypes[1].toLowerCase()) }}</span><div v-if="!loadingReservations && roomServiceReservations !== null" class="attendees_badge badge__big">{{ roomServiceReservations }}</div>
          </v-btn>
          <v-btn :value="activityReservationTypes[2]">
            <span>{{ $t("pages.reservation_list_types." + activityReservationTypes[2].toLowerCase()) }}</span><div v-if="!loadingReservations && inVenueReservations !== null" class="attendees_badge badge__big">{{ inVenueReservations }}</div>
          </v-btn>
        </v-btn-toggle>
      </div>

      <div class="reservation-tabs-margin">
        <v-tabs class="rounded" color="transparent" fixed-tabs v-model="tab">
          <v-tab v-for="item in items" :key="item.tab">
            {{ $t("reservations." + item.tab) }}
          </v-tab>
        </v-tabs>
      </div>

      <v-layout column class="app-scroll-section section-margins">
        <div
          class="justify-center margin-on-children"
          id="reservations"
          ref="reservations"
          v-if="filteredAndSortedReservations.length > 0 || loadingReservations"
          style="overflow-y: scroll; -webkit-overflow-scrolling: touch"
        >
          <v-layout row wrap class="my-0">
            <v-flex
              xs12
              sm6
              v-for="item in filteredAndSortedReservations"
              :key="item.reservation.id"
              class="my-2 margin-on-last-child"
            >
              <activity-reservation-list-item
                :reservation="item"
                @openDetails="openDetails(item)"
                :class="{'mx-1': $vuetify.breakpoint.smAndUp }"
              />
            </v-flex>
          </v-layout>

          <activity-reservation-modal
            :key="`${ keyModal }`"
            :openDetails="showDetailsDialog"
            :reservationId="reservationId"
            @closeDetails="
              showDetailsDialog = false;
              reservationId = null;
            "
            @goToReservation="openDetailsById"
            @reload="refreshReservations()"
          />

          <v-layout justify-center>
            <v-progress-circular
              v-if="loadingReservations"
              indeterminate
              color="primary darken-1"
              class="my-2"
            ></v-progress-circular>
            <span v-if="reachedEnd" class="user-text body-2 mt-4 mb-2">{{
              $t("reservations.no_more_reservations")
            }}</span>
          </v-layout>
        </div>
        <v-layout v-else column class="H1 grey-1--text pt-7 ml-3">
          {{ $t("no_reservations.base", { status: $t(`no_reservations.status.${items[tab].tab}.${selectedType === 'ALL' ? reservationTypes.ROOM_SERVICE : selectedType}`), type: $t(`no_reservations.type.${selectedType}`) }) }}
        </v-layout>
        <v-layout wrap class="justify-end flex-none" pr-2>
          <v-fade-transition mode="out-in">
            <v-btn
              v-if="scrolled"
              dark
              fixed
              small
              bottom
              color="white"
              class="floating primary--text text--lighten-1"
              @click="goToTop()"
            >
              <v-icon class="material-symbols-rounded">arrow_upward</v-icon>
            </v-btn>
          </v-fade-transition>
          <v-fade-transition mode="out-in">
            <refresh-button fixed v-if="!scrolled && !loadingReservations" @refresh="getAllReservations()" />
          </v-fade-transition>
        </v-layout>
      </v-layout>
    </v-layout>
  </v-container>
</template>

<script>
import moment from 'moment'
import { debounce } from 'lodash'
import { mapGetters } from 'vuex'
import routeNames from '@/router/routes'
import router from '@/router'
import { activityReservationStatus, reservationTypes } from '@/config'
import { ActivityReservationsRequests } from '@/services/activityReservations.requests'
import activityMixins from '@/mixins/Activity'
import routingMixins from '@/mixins/Routing'
const activityReservationsService = new ActivityReservationsRequests()

export default {
  data () {
    return {
      tab: null,
      keyModal: new Date(),
      items: [
        { 
          tab: 'upcoming', 
          filterByStatus: [
            activityReservationStatus.PENDING,
            activityReservationStatus.ACCEPTED,
            activityReservationStatus.PENDING_MODIFICATION,
            activityReservationStatus.ACCEPTED_BY_ACTIVITY_MODIFICATION,
            activityReservationStatus.PENDING_PAYMENT,
          ],
        },
        {
          tab: 'past',
          filterByStatus: [
            activityReservationStatus.REJECTED,
            activityReservationStatus.ATTENDED,
            activityReservationStatus.ABSENT,
            activityReservationStatus.CANCELLED,
            activityReservationStatus.CANCELLED_BY_ACTIVITY_MODIFICATION,
          ],
        },
        {
          tab: 'all',
          filterByStatus: [
            activityReservationStatus.PENDING,
            activityReservationStatus.ACCEPTED,
            activityReservationStatus.PENDING_MODIFICATION,
            activityReservationStatus.ACCEPTED_BY_ACTIVITY_MODIFICATION,
            activityReservationStatus.PENDING_PAYMENT,
            activityReservationStatus.REJECTED,
            activityReservationStatus.ATTENDED,
            activityReservationStatus.ABSENT,
            activityReservationStatus.CANCELLED,
            activityReservationStatus.CANCELLED_BY_ACTIVITY_MODIFICATION,
          ],
        },
      ],
      reservations: [],
      filteredAndSortedReservations: [],
      loadingReservations: false,
      offset: 0,
      limit: 50,
      reachedEnd: false,
      showDetailsDialog: false,
      reservationId: null,
      scrolled: false,
      debounceScrollHandler: null,
      activityReservationTypes: [
        'ALL',
        reservationTypes.ROOM_SERVICE,
        reservationTypes.IN_VENUE,
      ],
      reservationTypes,
      selectedType: 'ALL',
    }
  },
  components: {
    ActivityReservationModal: () =>
      import(
        '@/components/view-components/reservations/ActivityReservationModal.vue'
      ),
    ActivityReservationListItem: () => import('@/components/view-components/reservations/ActivityReservation.vue'),
    RefreshButton: () => import('@/components/app-components/RefreshButton.vue'),
  },
  created () {
    if (this.$route.query.reservationId) {
      this.openDetailsById(this.$route.query.reservationId)
      this.$store.commit('activityReservations/resetLastReservation')
    }
    this.$store.commit('activityReservations/reset')
    this.getAllReservations()
    this.debounceScrollHandler = debounce(this.handleScroll, 100)
  },
  beforeDestroy () {
    if (this.$refs.reservations) {
      this.$refs.reservations.removeEventListener('scroll', this.debounceScrollHandler)
    }
  },
  computed: {
    ...mapGetters({
      getDashboardName: 'service/getDashboardName',
    }),
    totalReservations () {
      if (!this.reservations.length) {
        return null
      } else {
        return this.reservations.length > 99 ? ':)' : this.reservations.length
      }
    },
    roomServiceReservations () {
      if (!this.reservations.length) {
        return null
      } else {
        const reservations = this.reservations.filter((reservation) => {
          return reservation.reservation.type === reservationTypes.ROOM_SERVICE
        })
        return reservations.length > 99 ? ':)' : reservations.length
      }
    },
    inVenueReservations () {
      if (!this.reservations.length) {
        return null
      } else {
        const reservations = this.reservations.filter((reservation) => {
          return reservation.reservation.type === reservationTypes.IN_VENUE
        })
        return reservations.length > 99 ? ':)' : reservations.length
      }
    },
  },
  methods: {
    handleScroll (event) {
      const o = document.getElementById('reservations')
      var y = event.target.scrollTop
      if (y >= o.offsetHeight) {
        this.scrolled = true
      } else {
        this.scrolled = false
      }
    },
    exit () {
      router.push({ name: routeNames.main.name })
    },
    filterReservations () {
      const filterByStatus = this.items[this.tab].filterByStatus
      const filteredReservations = this.reservations.filter((reservation) => {
        if (this.selectedType !== 'ALL') {
          return reservation.reservation.type === this.selectedType && filterByStatus.includes(reservation.reservation.status)
        } else {
          return filterByStatus.includes(reservation.reservation.status)
        }
      })
      const sortedReservations = filteredReservations.sort((a, b) => {
        const aMoment = moment(a.reservation.acceptedDate || a.reservation.requestedDate).add(a.reservation.acceptedTime || a.reservation.requestedTime, 'ms')
        const bMoment = moment(b.reservation.acceptedDate || b.reservation.requestedDate).add(b.reservation.acceptedTime || b.reservation.requestedTime, 'ms')
        if (aMoment.isBefore(bMoment)) {
          return this.tab === 0 ? -1 : 1
        } else if (aMoment.isAfter(bMoment)) {
          return this.tab === 0 ? 1 : -1
        } else {
          return 0
        }
      })
      this.filteredAndSortedReservations = sortedReservations
    },
    async getAllReservations () {
      let continueNextList = true
      this.loadingReservations = true
      let firstGet = true
      while (continueNextList) {
        await this.getReservations(firstGet).then(({ hasNext }) => {
          continueNextList = hasNext
        })
        if (firstGet) {
          firstGet = false
        }
        this.offset = this.offset + this.limit
      }
      this.filterReservations()
      if (this.$refs.reservations) {
        this.$refs.reservations.addEventListener('scroll', this.debounceScrollHandler)
      }
      this.loadingReservations = false
    },
    getReservations (resetList = false) {
      return new Promise((resolve, reject) => {
        if (resetList) {
          this.reservations = []
          this.filteredAndSortedReservations = []
          this.offset = 0
        }
        activityReservationsService
          .listReservations({
            limit: this.limit,
            offset: this.offset,
          })
          .then(async (response) => {
            this.reservations.push.apply(
              this.reservations,
              response.reservations
            )
            resolve(response)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },
    refreshReservations () {
      this.offset = 0
      this.reachedEnd = false
      this.reservations = []
      this.getAllReservations()
    },
    goToTop () {
      const scrollDuration = 400
      const reservationList = document.getElementById('reservations')
      var scrollStep = -reservationList.scrollTop / (scrollDuration / 15)
      var scrollInterval = setInterval(function () {
        if (reservationList.scrollTop !== 0) {
          reservationList.scrollBy(0, scrollStep)
        } else clearInterval(scrollInterval)
      }, 15)
    },
    openDetailsById (id) {
      this.reservationId = id
      this.showDetailsDialog = true
      this.keyModal = new Date()
      
    },
    openDetails (item) {
      this.reservationId = item.reservation.id
      const query = this.buildQueryStringFromObject({ reservationId: this.reservationId })
      history.replaceState(null, '', query)
      this.showDetailsDialog = true
    },
  },
  watch: {
    tab () {
      this.filterReservations()
      this.goToTop()
    },
    selectedType () {
      this.filterReservations()
      this.goToTop()
    },
  },
  mixins: [
    activityMixins,
    routingMixins,
  ],
}
</script>
<style lang="scss">
.v-btn-toggle.reservation-list > .v-btn:not(.v-item--active) {
  color: var(--v-grey-2-b-base) !important;
  .attendees_badge {
    color: var(--v-grey-2-b-base) !important;
    background-color: var(--v-grey-3-base) !important;
  }
}

.v-btn-toggle.reservation-list {
  >.v-btn {
    background: none !important;
    text-transform: none !important;
    font-family: Urbanist;
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    letter-spacing: 0.25px;
    padding: 0px 7px !important;
    &:before, .v-ripple__container {
      display: none !important;
    }
    &.v-btn--active {
      color: var(--v-primary-lighten1);
    }
  }
}
.reservation-tabs-margin {
  margin-bottom: 10px;
}
.rounded {
  flex: none;
  .v-tabs-bar {
    border-radius: 10px;
    border: 1px solid var(--v-grey-2-base);
    height: 33px;
  }
  .v-tab {
    font-family: 'Mukta Mahee';
    font-size: 15px;
    font-style: normal;
    font-weight: 500;
    letter-spacing: 0.25px;
    text-transform: none;
    &.v-tab--active {
      border-radius: 10px;
      color: white !important;
      background: var(--v-primary-lighten1);
    }
    &:not(.v-tab--active) {
      color: var(--v-grey-2-base) !important;
    }
  }
}
</style>
